import { type FC, type MouseEvent, useState } from "react";

import { Button, Icon, Text, Tooltip, toastError } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetEntityTaxForm,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  acceptCertificationsForPrimaryAndSigner,
  acceptTaxFileForm,
} from "~/v2/api/rest/entityOnboarding";
import { Radio } from "./Radio";

const TAX_FORM_LABEL = {
  certifications_and_resolutions_statement: "Certifications and Resolutions",
  w_8ben: "W-8BEN",
} as const;

export type ViewAndAcceptProps = {
  accountId: string;
  ownerId: string;
  taxForm: "w_8ben" | "certifications_and_resolutions_statement";
  isAccepted: boolean;
  onChange: (isAccepted: boolean) => void;
  errorMessage?: string;
  signerPartyId?: string;
};

export const ViewAndAccept: FC<ViewAndAcceptProps> = ({
  accountId,
  ownerId,
  taxForm,
  onChange,
  errorMessage,
  isAccepted,
  signerPartyId,
}) => {
  const taxFormLabel = TAX_FORM_LABEL[taxForm];

  const [isPreviewViewed, setIsPreviewViewed] = useState(false);

  const { data: previewBlob } = useGetEntityTaxForm({
    accountId,
    ownerId,
    taxForm,
    options: {
      enabled: !!ownerId && !!accountId,
    },
  });

  const mutation = useMutationRequest(
    () => {
      if (taxForm === "certifications_and_resolutions_statement") {
        if (!signerPartyId) {
          throw new Error("A required signer ID is missing.");
        }
        return acceptCertificationsForPrimaryAndSigner({
          accountId,
          legalEntityOwnerId: ownerId,
          signerPartyId,
        });
      }

      // Handle W-8BEN form
      return acceptTaxFileForm({
        accountId,
        ownerId,
        form: taxForm,
      });
    },
    {
      onSuccess: () => {
        onChange(true);
      },
      onError: (error) => {
        const errorMsg = getErrorMessage(error);
        toastError(`Error accepting ${taxFormLabel}, ${errorMsg}`);
        onChange(false);
      },
    },
  );

  const onClickPreview = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!previewBlob) {
      toastError(
        `Error viewing ${taxFormLabel}, please try again in a moment.`,
      );
      return;
    }

    const binaryData = [];
    binaryData.push(previewBlob);
    const fileURL = window.URL.createObjectURL(
      new Blob(binaryData, { type: "application/pdf" }),
    );
    window.open(fileURL);
    setIsPreviewViewed(true);
  };

  // We don't allow users to un-accept :(
  const isAcceptDisabled =
    !isPreviewViewed || mutation.isPending || mutation.isSuccess;

  const onClickAccept = (isChecked: boolean) => {
    if (isAcceptDisabled || !isChecked) {
      return;
    }
    mutation.mutate(true);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Text className="block font-medium text-sm">
          {taxForm === "w_8ben" && "Form "}
          {taxFormLabel}
        </Text>
        {errorMessage && (
          <Text className="truncate text-rose-500 text-xs">{errorMessage}</Text>
        )}
      </div>

      <Text className="font-normal text-gray-600 text-sm">
        I confirm and certify that the information provided in the{" "}
        {taxFormLabel} form is true, accurate, and complete. By clicking "I
        Agree" below, I acknowledge that I am electronically signing the{" "}
        {taxFormLabel} form.
      </Text>
      <Button
        type="button"
        variant="alternate"
        onClick={onClickPreview}
        className="max-w-fit"
      >
        View Form {taxFormLabel}
        <Icon name="ArrowTopRightOnSquare" className="ml-2" />
      </Button>
      <Tooltip
        content={
          isPreviewViewed
            ? isAccepted
              ? "Accepted"
              : "Click to accept document"
            : "Must view document first."
        }
      >
        <Radio
          name={taxForm}
          label={`I agree to electronically signing the ${taxFormLabel} form.`}
          isChecked={isAccepted}
          disabled={isAcceptDisabled}
          onChange={onClickAccept}
        />
      </Tooltip>
    </div>
  );
};
